import React, { useState } from "react";
import { Copy, Check, Link2 } from "lucide-react";
import CodeChangeForm from "./CodeChangeForm";
import { fmtBps } from "./format";

/**
 * The two links a practitioner actually shares, and the code behind them.
 *
 * THE LINKS ARE THE SERVER'S. They are built from `APP_PUBLIC_URL` + the SPA's real routes
 * and arrive whole on `account.shareLinks`; a link concatenated from `window.location`
 * would, on a practice's own subdomain, point at that practice's site instead of the
 * platform — a referral link that refers nobody.
 *
 * `rates` are ADVISORY and say so: the accrual snapshots the rate at capture, so a
 * commission written last month keeps the rate it was written with.
 */
function CopyRow({ label, url, onCopy, copied, testId, copyTestId, copyLabel, copiedLabel }) {
  return (
    <div className="mt-3">
      <div className="text-[11px] uppercase tracking-[0.16em] text-sol-gold/80">{label}</div>
      <div className="mt-1 flex items-center gap-2">
        <code className="flex-1 min-w-0 bg-white/10 rounded-lg px-3 py-2 text-xs break-all" data-testid={testId}>
          {url}
        </code>
        <button
          type="button"
          onClick={onCopy}
          data-testid={copyTestId}
          className="shrink-0 inline-flex items-center gap-1 bg-sol-gold text-sol-midnight rounded-lg px-3 py-2 text-xs font-semibold"
        >
          {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
          {copied ? copiedLabel : copyLabel}
        </button>
      </div>
    </div>
  );
}

export default function ShareLinkCard({ account, program, t, tv, onAccountChanged }) {
  const [copied, setCopied] = useState("");

  const copy = async (which, url) => {
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      // A clipboard the browser refuses (no permission, insecure origin) is not a failure
      // worth a toast: the URL is on screen, selectable, and the practitioner can take it
      // by hand. Claiming "copied" when nothing was copied would be the lie.
      return;
    }
    setCopied(which);
  };

  return (
    <section
      data-testid="affiliate-share-card"
      className="bg-gradient-to-br from-sol-midnight to-[#1a2440] text-sol-ivory rounded-2xl p-6"
    >
      <h2 className="font-display text-xl flex items-center gap-2">
        <Link2 className="w-4 h-4 text-sol-gold" /> {t("aff.share.title")}
      </h2>
      <p className="text-xs text-sol-ivory/60 mt-1">
        {t("aff.share.desc", { days: program.attributionWindowDays })}
      </p>

      <CopyRow
        label={t("aff.share.signup")}
        url={account.shareLinks.signup}
        onCopy={() => copy("signup", account.shareLinks.signup)}
        copied={copied === "signup"}
        testId="affiliate-link-signup"
        copyTestId="copy-affiliate-link"
        copyLabel={t("aff.share.copy")}
        copiedLabel={t("aff.share.copied")}
      />
      <CopyRow
        label={t("aff.share.marketplace")}
        url={account.shareLinks.marketplace}
        onCopy={() => copy("marketplace", account.shareLinks.marketplace)}
        copied={copied === "marketplace"}
        testId="affiliate-link-marketplace"
        copyTestId="copy-affiliate-marketplace-link"
        copyLabel={t("aff.share.copy")}
        copiedLabel={t("aff.share.copied")}
      />

      <div className="mt-4 text-xs text-sol-ivory/60">
        {t("aff.share.codeLabel")} <strong className="text-sol-ivory" data-testid="affiliate-code-value">{account.code}</strong>
      </div>
      <p className="mt-1 text-xs text-sol-ivory/60">
        {t("aff.share.rates", {
          saas: fmtBps(program.rates.saasBps),
          marketplace: fmtBps(program.rates.marketplaceBps),
        })}
      </p>

      <CodeChangeForm account={account} t={t} tv={tv} onAccountChanged={onAccountChanged} />
    </section>
  );
}
